import React, { useState, useEffect } from 'react';
import { 
    Plus, Edit3, Trash2, CheckCircle, AlertTriangle, Bed, Settings, ChevronDown, ChevronRight, Layers, Calculator, Save 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const CreationChambre = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const company_id = user?.company_id || user?.companyId;

  const [chambres, setChambres] = useState([]);
  const [categories, setCategories] = useState([]);
  const [prestationsDisponibles, setPrestationsDisponibles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [expandedRows, setExpandedRows] = useState({});
  const [composantsChambres, setComposantsChambres] = useState({});
  const [loadingComposants, setLoadingComposants] = useState({});
  const [companyInfo, setCompanyInfo] = useState({ regime_tva_recuperable: 1, taux_tva: 18.0 });

  // Champs de base du formulaire chambre (avec type_occupation)
  const [formData, setFormData] = useState({ 
    numero: '', 
    categorie_id: '', 
    tarif_base: '', 
    type_occupation: 'SINGLE',
    statut: 'DISPONIBLE' 
  });

  // États pour les 3 blocs (Catalogue, Saisie/Ajout, Panier)
  const [lignesTarifaires, setLignesTarifaires] = useState([]);
  const [selectedPrestation, setSelectedPrestation] = useState(null);
  const [quantiteSaisie, setQuantiteSaisie] = useState(1);
  const [montantSaisie, setMontantSaisie] = useState("");
  const [searchTermPresta, setSearchTermPresta] = useState('');

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [modalConfig, setModalConfig] = useState({ show: false, action: null, message: '', id: null });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resChambres, resCategories, resCompany, resComposants] = await Promise.all([
        API.get('/hebergement/chambres').catch(() => ({ data: [] })),
        API.get('/hebergement/categories').catch(() => ({ data: [] })),
        company_id ? API.get(`/company/${company_id}`).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        API.get('/hebergement/composants').catch(() => ({ data: [] }))
      ]);
      
      if (resCompany.data?.success && resCompany.data.data) {
        setCompanyInfo({
          regime_tva_recuperable: resCompany.data.data.regime_tva_recuperable ?? 1,
          taux_tva: resCompany.data.data.taux_tva ?? 18.0
        });
      }

      const rawComposants = resComposants.data;
      let listCompBruts = [];
      if (Array.isArray(rawComposants)) {
        listCompBruts = rawComposants;
      } else if (rawComposants && typeof rawComposants === 'object') {
        listCompBruts = rawComposants.composants || rawComposants.data || rawComposants.items || [];
      }

      const listeComposants = listCompBruts.map(c => ({
        id: c.id,
        nom: c.libelle || c.nom || c.designation || 'Sans nom',
        prix_unitaire: Number(c.prix_defaut || c.prix_unitaire || 0),
        soumis_tva: c.soumis_tva === 1 || c.soumis_tva === true,
        taux_tva: Number(c.taux_tva || resCompany.data?.data?.taux_tva || 18.0)
      }));
      setPrestationsDisponibles(listeComposants);

      const rawChambres = resChambres.data;
      let chambresBrutes = [];
      if (Array.isArray(rawChambres)) {
        chambresBrutes = rawChambres;
      } else if (rawChambres && typeof rawChambres === 'object') {
        chambresBrutes = rawChambres.chambres || rawChambres.data || rawChambres.items || [];
      }
      setChambres(chambresBrutes);

      const rawCategories = resCategories.data;
      let categoriesBrutes = [];
      if (Array.isArray(rawCategories)) {
        categoriesBrutes = rawCategories;
      } else if (rawCategories && typeof rawCategories === 'object') {
        categoriesBrutes = rawCategories.categories || rawCategories.data || rawCategories.items || [];
      }
      setCategories(categoriesBrutes);

    } catch (err) {
      console.error("Erreur chargement données chambres:", err);
      showToast("Erreur de chargement des données", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [company_id]);

  const toggleRowExpansion = async (chambreId) => {
    const isCurrentlyExpanded = !!expandedRows[chambreId];
    if (isCurrentlyExpanded) {
      setExpandedRows(prev => ({ ...prev, [chambreId]: false }));
      return;
    }

    if (!composantsChambres[chambreId]) {
      setLoadingComposants(prev => ({ ...prev, [chambreId]: true }));
      try {
        const res = await API.get(`/hebergement/chambres/${chambreId}/composants`);
        const rawComp = res.data;
        let compList = [];
        if (Array.isArray(rawComp)) {
          compList = rawComp;
        } else if (rawComp && typeof rawComp === 'object') {
          compList = rawComp.composants || rawComp.data || rawComp.items || [];
        }
        setComposantsChambres(prev => ({ ...prev, [chambreId]: compList }));
      } catch (err) {
        setComposantsChambres(prev => ({ ...prev, [chambreId]: [] }));
      } finally {
        setLoadingComposants(prev => ({ ...prev, [chambreId]: false }));
      }
    }
    setExpandedRows(prev => ({ ...prev, [chambreId]: true }));
  };

  const handleSelectPrestation = (p) => {
    setSelectedPrestation(p);
    setQuantiteSaisie(1);
    setMontantSaisie(p.prix_unitaire);
  };

  const handleQtyChange = (val) => {
    setQuantiteSaisie(val);
    const qte = parseFloat(val);
    if (selectedPrestation && !isNaN(qte) && qte > 0) {
      setMontantSaisie(Math.round(qte * selectedPrestation.prix_unitaire));
    } else {
      setMontantSaisie("");
    }
  };

  const confirmAddLine = () => {
    if (!selectedPrestation) return showToast("Veuillez sélectionner une prestation dans la liste", "error");
    const qteNum = parseFloat(quantiteSaisie);
    const montantNum = parseFloat(montantSaisie);

    if (isNaN(qteNum) || qteNum <= 0) return showToast("La quantité doit être supérieure à 0", "error");
    if (isNaN(montantNum) || montantNum < 0) return showToast("Le montant doit être valide", "error");
    if (lignesTarifaires.find(l => l.prestation_id === selectedPrestation.id)) return showToast("Cette prestation est déjà dans le panier", "error");

    const newLine = {
      id: `LIG-${Date.now()}`,
      prestation_id: selectedPrestation.id,
      nom: selectedPrestation.nom,
      prix_unitaire: montantNum / qteNum,
      quantite: qteNum,
      soumis_tva: selectedPrestation.soumis_tva,
      taux_tva: selectedPrestation.taux_tva
    };
    
    setLignesTarifaires([...lignesTarifaires, newLine]);
    setSelectedPrestation(null);
    setQuantiteSaisie(1);
    setMontantSaisie("");
  };

  const isEntrepriseSousTva = companyInfo.regime_tva_recuperable === 1;

  const totauxTarifaires = lignesTarifaires.reduce((acc, ligne) => {
    const montantLigneTTC = Number(ligne.prix_unitaire) * Number(ligne.quantite);
    if (isEntrepriseSousTva && ligne.soumis_tva && ligne.taux_tva > 0) {
      const montantHT = montantLigneTTC / (1 + (ligne.taux_tva / 100));
      acc.ht += montantHT;
      acc.tva += montantLigneTTC - montantHT;
    } else {
      acc.ht += montantLigneTTC;
    }
    acc.ttc += montantLigneTTC;
    return acc;
  }, { ht: 0, tva: 0, ttc: 0 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.numero.trim() || !formData.categorie_id) {
      showToast("Le numéro et la catégorie de la chambre sont obligatoires", "error");
      return;
    }

    if (lignesTarifaires.length === 0) {
      showToast("Veuillez ajouter au moins un paramétrage de prix dans le panier avant d'enregistrer.", "error");
      return;
    }

    setLoading(true);
    try {
      const payloadChambre = { 
        numero: formData.numero.toUpperCase().trim(), 
        categorie_id: formData.categorie_id,
        tarif_base: Math.round(totauxTarifaires.ttc),
        type_occupation: formData.type_occupation,
        statut: formData.statut
      };

      let targetChambreId = editingId;

      if (editingId) {
        await API.put(`/hebergement/chambres/${editingId}`, payloadChambre);
        showToast("Chambre mise à jour avec succès");
      } else {
        const resCreate = await API.post('/hebergement/chambres', payloadChambre);
        targetChambreId = resCreate.data?.id || resCreate.data?.chambre?.id || resCreate.data?.data?.id;
        showToast("Chambre créée avec succès");
      }

      if (targetChambreId) {
        const itemsFormates = lignesTarifaires.map(l => ({
          prestation_id: l.prestation_id,
          designation: l.nom,
          quantite: l.quantite,
          prix_unitaire: l.prix_unitaire,
          soumis_tva: l.soumis_tva ? 1 : 0,
          taux_tva: l.taux_tva
        }));

        await API.post('/hebergement/chambres/save-config', {
          chambre_id: targetChambreId,
          items: itemsFormates,
          tarif_total: Math.round(totauxTarifaires.ttc),
          montant_ht: Math.round(totauxTarifaires.ht),
          montant_tva: Math.round(totauxTarifaires.tva)
        }).catch(err => console.warn("Avertissement config:", err));
      }

      closeForm();
      fetchData();
    } catch (err) {
      console.error("Erreur enregistrement chambre :", err);
      const errorMsg = err.response?.data?.error || "Erreur lors de l'enregistrement";
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = async (item) => {
    setEditingId(item.id);
    setFormData({
      numero: item.numero,
      categorie_id: item.categorie_id,
      tarif_base: item.tarif_base,
      type_occupation: item.type_occupation || 'SINGLE',
      statut: item.statut
    });
    setIsAdding(true);
    setLignesTarifaires([]);

    try {
      const res = await API.get(`/hebergement/chambres/${item.id}/composants`);
      const rawCompEdit = res.data;
      let compEditList = [];
      if (Array.isArray(rawCompEdit)) {
        compEditList = rawCompEdit;
      } else if (rawCompEdit && typeof rawCompEdit === 'object') {
        compEditList = rawCompEdit.composants || rawCompEdit.data || rawCompEdit.items || [];
      }

      if (compEditList.length > 0) {
        const mapped = compEditList.map((l, idx) => ({
          id: `LIG-EDIT-${idx}`,
          prestation_id: l.prestation_id || l.composant_id || l.id,
          nom: l.designation || l.nom || 'Prestation',
          prix_unitaire: Number(l.prix || l.prix_unitaire || 0) / Number(l.quantite || 1),
          quantite: Number(l.quantite || 1),
          soumis_tva: l.soumis_tva === 1 || l.soumis_tva === true,
          taux_tva: Number(l.taux_tva || 18)
        }));
        setLignesTarifaires(mapped);
      }
    } catch (e) {
      console.warn("Impossible de charger les composants existants");
    }
  };

  const handleDeleteClick = (id) => {
    setModalConfig({
      show: true,
      action: () => executeDelete(id),
      message: "Voulez-vous vraiment supprimer cette chambre physique ?",
      id
    });
  };

  const executeDelete = async (id) => {
    setLoading(true);
    try {
      await API.delete(`/hebergement/chambres/${id}`);
      showToast("Chambre supprimée avec succès");
      setModalConfig({ show: false, action: null, message: '', id: null });
      fetchData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Impossible de supprimer cette chambre.";
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const closeForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ numero: '', categorie_id: '', tarif_base: '', type_occupation: 'SINGLE', statut: 'DISPONIBLE' });
    setLignesTarifaires([]);
    setSelectedPrestation(null);
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <main style={{ flex: 1, overflowY: 'auto', background: '#f1f5f9', position: 'relative' }}>
        
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Bed size={22} color="#2563eb" /> GESTION DES CHAMBRES PHYSIQUES
            </h1>
            <div style={{ color: '#64748b', fontSize: '13px' }}>
              Inventaire des chambres et configuration tarifaire intégrée
            </div>
          </div>
          <div>
            {!isAdding && !editingId && (
              <button onClick={() => setIsAdding(true)} style={btnPrimaryStyle}>
                <Plus size={18} /> Nouvelle Chambre & Paramétrage
              </button>
            )}
          </div>
        </header>

        <div style={{ padding: '30px 40px' }}>

          {(isAdding || editingId) && (
            <div style={{ ...contentCardStyle, padding: '25px', marginBottom: '25px', border: '2px solid #2563eb' }}>
              <h3 style={{ fontSize: '15px', marginBottom: '15px', fontWeight: '900', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Settings size={18} color="#2563eb"/> {editingId ? 'Modifier la chambre et ses prestations' : 'Création rapide de chambre & composition des tarifs'}
              </h3>

              <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '20px' }}>
                  <div style={{ flex: 1, minWidth: '130px' }}>
                    <label style={labelStyle}>NUMÉRO / NOM *</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Ex: 101"
                      value={formData.numero} 
                      onChange={(e) => setFormData({...formData, numero: e.target.value})} 
                      required 
                    />
                  </div>
                  <div style={{ flex: 1.5, minWidth: '180px' }}>
                    <label style={labelStyle}>CATÉGORIE *</label>
                    <select 
                      style={inputStyle} 
                      value={formData.categorie_id} 
                      onChange={(e) => setFormData({...formData, categorie_id: e.target.value})} 
                      required
                    >
                      <option value="">-- Choisir une catégorie --</option>
                      {categories.map((cat, idx) => (
                        <option key={cat.id || idx} value={cat.id}>{cat.nom}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <label style={labelStyle}>TYPE D'OCCUPATION</label>
                    <select 
                      style={inputStyle} 
                      value={formData.type_occupation} 
                      onChange={(e) => setFormData({...formData, type_occupation: e.target.value})}
                    >
                      <option value="SINGLE">Single</option>
                      <option value="DOUBLE">Double</option>
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: '130px' }}>
                    <label style={labelStyle}>STATUT INITIAL</label>
                    <select 
                      style={inputStyle} 
                      value={formData.statut} 
                      onChange={(e) => setFormData({...formData, statut: e.target.value})}
                    >
                      <option value="DISPONIBLE">Disponible</option>
                      <option value="OCCUPE">Occupé</option>
                      <option value="RESERVE">Réservé</option>
                    </select>
                  </div>
                </div>

                {/* 3 TABLEAUX */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.1fr 1.2fr', gap: '15px', marginBottom: '20px' }}>
                  
                  {/* TABLEAU 1 : Liste filtrée (masque les prestations déjà dans le panier) */}
                  <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: '11px', fontWeight: '900', color: '#1e293b', marginBottom: '8px' }}>
                      1. LISTE DES PRESTATIONS (CLIQUER)
                    </div>
                    <input 
                      type="text" 
                      placeholder="Filtrer..." 
                      style={{ width: '100%', padding: '6px 10px', fontSize: '11px', borderRadius: '6px', border: '1px solid #cbd5e1', marginBottom: '8px', boxSizing: 'border-box' }}
                      onChange={(e) => setSearchTermPresta(e.target.value)}
                    />
                    <div style={{ background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', maxHeight: '180px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          {prestationsDisponibles
                            .filter(p => !lignesTarifaires.some(l => l.prestation_id === p.id)) // Masque si déjà dans le panier
                            .filter(p => p.nom.toLowerCase().includes((searchTermPresta || '').toLowerCase()))
                            .map(p => {
                              const isSelected = selectedPrestation?.id === p.id;
                              return (
                                <tr 
                                  key={p.id} 
                                  onClick={() => handleSelectPrestation(p)}
                                  style={{ cursor: 'pointer', background: isSelected ? '#eff6ff' : 'transparent', borderBottom: '1px solid #f1f5f9' }}
                                >
                                  <td style={{ padding: '8px 10px', fontSize: '12px', fontWeight: isSelected ? '800' : '600', color: isSelected ? '#2563eb' : '#334155' }}>
                                    {p.nom}
                                  </td>
                                  <td style={{ padding: '8px 10px', fontSize: '12px', fontWeight: '800', color: '#059669', textAlign: 'right' }}>
                                    {Math.round(p.prix_unitaire).toLocaleString()} F
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* TABLEAU 2 */}
                  <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '900', color: '#1e293b', marginBottom: '8px' }}>
                        2. PARAMÉTRAGE & AJOUT
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: '800', color: '#2563eb', marginBottom: '8px', background: '#e0f2fe', padding: '6px', borderRadius: '6px' }}>
                        {selectedPrestation ? selectedPrestation.nom : '👉 Sélectionnez une prestation'}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '10px', fontWeight: '800', color: '#64748b' }}>Qté</label>
                          <input 
                            type="number" 
                            style={{ width: '100%', padding: '6px', fontSize: '13px', fontWeight: '800', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', boxSizing: 'border-box' }}
                            value={quantiteSaisie}
                            onChange={(e) => handleQtyChange(e.target.value)}
                            min="0.1"
                            step="any"
                          />
                        </div>
                        <div style={{ flex: 1.5 }}>
                          <label style={{ fontSize: '10px', fontWeight: '800', color: '#059669' }}>Montant TTC modifiable</label>
                          <input 
                            type="number" 
                            style={{ width: '100%', padding: '6px', fontSize: '13px', fontWeight: '800', textAlign: 'center', border: '1px solid #059669', borderRadius: '6px', background: '#f0fdf4', color: '#059669', boxSizing: 'border-box' }}
                            value={montantSaisie}
                            onChange={(e) => setMontantSaisie(e.target.value)}
                            min="0"
                            step="any"
                          />
                        </div>
                      </div>
                    </div>
                    <button 
                      type="button" 
                      onClick={confirmAddLine}
                      disabled={!selectedPrestation}
                      style={{ width: '100%', padding: '8px', background: selectedPrestation ? '#2563eb' : '#cbd5e1', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '800', cursor: selectedPrestation ? 'pointer' : 'not-allowed', fontSize: '11px' }}
                    >
                      + Ajouter au Panier
                    </button>
                  </div>

                  {/* TABLEAU 3 (Panier) */}
                  <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: '11px', fontWeight: '900', color: '#1e293b', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                      <span>3. PANIER ({lignesTarifaires.length})</span>
                      <span style={{ color: '#059669' }}>{Math.round(totauxTarifaires.ttc).toLocaleString()} F</span>
                    </div>
                    <div style={{ background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', maxHeight: '180px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          {lignesTarifaires.length === 0 ? (
                            <tr>
                              <td colSpan="3" style={{ textAlign: 'center', padding: '20px', color: '#ef4444', fontSize: '11px', fontWeight: '700' }}>
                                Panier vide (Obligatoire)
                              </td>
                            </tr>
                          ) : (
                            lignesTarifaires.map(l => (
                              <tr key={l.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '6px 8px', fontSize: '11px', fontWeight: '700' }}>
                                  {l.nom} <span style={{ color: '#64748b' }}>(x{l.quantite})</span>
                                </td>
                                <td style={{ padding: '6px 8px', fontSize: '11px', fontWeight: '800', color: '#059669', textAlign: 'right' }}>
                                  {Math.round(l.prix_unitaire * l.quantite).toLocaleString()} F
                                </td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', width: '30px' }}>
                                  <button 
                                    type="button" 
                                    onClick={() => {
                                      setLignesTarifaires(lignesTarifaires.filter(x => x.id !== l.id));
                                      if (selectedPrestation?.id === l.prestation_id) setSelectedPrestation(null);
                                    }} 
                                    style={{ background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '2px 4px', cursor: 'pointer' }}
                                  >
                                    <Trash2 size={10}/>
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button type="submit" disabled={loading} style={btnSubmitStyle}>{loading ? '...' : 'Enregistrer la chambre'}</button>
                  <button type="button" onClick={closeForm} style={btnCancelStyle}>Annuler</button>
                </div>
              </form>
            </div>
          )}

          {/* TABLEAU GLOBAL */}
          <div style={contentCardStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ ...thStyle, width: '40px' }}></th>
                  <th style={thStyle}>N° Chambre</th>
                  <th style={thStyle}>Catégorie</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Tarif Base / Total</th>
                  <th style={thStyle}>Statut Actuel</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {chambres.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Aucune chambre physique enregistrée.
                    </td>
                  </tr>
                ) : (
                  chambres.map((item, idx) => {
                    const uniqueKey = item.id ? `chambre-${item.id}-${idx}` : `chambre-idx-${idx}`;
                    const isExpanded = !!expandedRows[item.id];
                    const componentsList = composantsChambres[item.id] || [];

                    return (
                      <React.Fragment key={uniqueKey}>
                        <tr style={{ borderBottom: '1px solid #f1f5f9', background: isExpanded ? '#f8fafc' : 'transparent' }}>
                          <td style={{ ...tdStyle, textAlign: 'center', width: '40px' }}>
                            <button 
                              onClick={() => toggleRowExpansion(item.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                            </button>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: '800' }}>
                            <code style={codeStyle}>{item.numero}</code>
                          </td>
                          <td style={tdStyle}>{item.categorie_nom || '---'}</td>
                          <td style={tdStyle}>
                            <span style={{ fontWeight: '700', color: item.type_occupation === 'DOUBLE' ? '#0369a1' : '#475569' }}>
                              {item.type_occupation || 'SINGLE'}
                            </span>
                          </td>
                          <td style={tdStyle}>{Number(item.tarif_base || 0).toLocaleString()} F</td>
                          <td style={tdStyle}>
                            <span style={badgeStyle(item.statut)}>{item.statut}</span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                              <button 
                                onClick={() => handleEditClick(item)} 
                                style={actionBtnStyle('#2563eb')}
                                title="Modifier"
                              >
                                <Edit3 size={14}/> Modifier
                              </button>
                              
                              <button onClick={() => handleDeleteClick(item.id)} style={actionBtnStyle('#ef4444')} title="Supprimer">
                                <Trash2 size={14}/> Supprimer
                              </button>
                            </div>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr>
                            <td colSpan={7} style={{ background: '#f8fafc', padding: '15px 30px', borderBottom: '1px solid #e2e8f0' }}>
                              <div style={{ background: 'white', padding: '15px 20px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                                <div style={{ fontSize: '12px', fontWeight: '800', color: '#1e293b', marginBottom: '10px' }}>
                                  Composition tarifaire pour la chambre {item.numero}
                                </div>
                                {loadingComposants[item.id] ? (
                                  <p style={{ fontSize: '12px', color: '#64748b' }}>Chargement...</p>
                                ) : componentsList.length === 0 ? (
                                  <p style={{ fontSize: '12px', color: '#64748b' }}>Aucun composant spécifique configuré.</p>
                                ) : (
                                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#334155' }}>
                                    {componentsList.map((c, i) => (
                                      <li key={i}>{c.designation || c.nom} - Qté: {c.quantite} - <strong style={{ color: '#059669' }}>{Number(c.prix || c.prix_unitaire || 0).toLocaleString()} F</strong></li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {modalConfig.show && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
              <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '50%' }}><AlertTriangle size={24} /></div>
              <h2 style={{ margin: 0, fontSize: '18px' }}>Confirmation de suppression</h2>
            </div>
            <p style={{ color: '#475569', marginBottom: '30px' }}>{modalConfig.message}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalConfig({ show: false, action: null, message: '', id: null })} style={btnSecondaryStyle}>Annuler</button>
              <button onClick={modalConfig.action} style={btnDangerStyle}>{loading ? '...' : 'Supprimer'}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes slideInToast { from { transform: translate(-50%, -20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }`}</style>
    </div>
  );
};

// --- STYLES ---
const headerStyle = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const contentCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden' };
const btnPrimaryStyle = { background: '#2563eb', color: 'white', border: 'none', padding: '10px 22px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700' };
const btnSecondaryStyle = { border: '1px solid #e2e8f0', padding: '10px 15px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#475569', background: 'white' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', marginTop: '4px', background: '#fff', boxSizing: 'border-box' };
const labelStyle = { fontSize: '11px', fontWeight: '900', color: '#475569' };
const thStyle = { padding: '15px 20px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '800', textAlign: 'left' };
const tdStyle = { padding: '12px 20px', fontSize: '13px', color: '#334155' };
const codeStyle = { fontSize: '12px', background: '#f1f5f9', padding: '4px 8px', borderRadius: '4px', color: '#2563eb', fontWeight: '900' };
const btnSubmitStyle = { padding: '10px 25px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' };
const btnCancelStyle = { padding: '10px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' };
const actionBtnStyle = (color) => ({ display: 'flex', alignItems: 'gap: 6px', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${color}30`, background: `${color}10`, color: color, fontSize: '11px', fontWeight: '900', cursor: 'pointer' });
const badgeStyle = (statut) => {
  let bg = '#dcfce7', color = '#15803d';
  if (statut === 'OCCUPE') { bg = '#fef3c7'; color = '#d97706'; }
  else if (statut === 'RESERVE') { bg = '#e0f2fe'; color = '#0369a1'; }
  return { padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '900', background: bg, color: color };
};
const toastContainerStyle = { position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, color: 'white', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '800', animation: 'slideInToast 0.3s ease-out' };
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContentStyle = { background: 'white', padding: '30px', borderRadius: '12px', width: '400px', maxWidth: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' };
const btnDangerStyle = { background: '#ef4444', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' };

export default CreationChambre;